<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Landmark class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Editar Fuente de Fondos' : 'Nueva Fuente de Fondos' }}
          </h2>
          <p class="text-xs text-slate-500">Origen de los fondos (Ej: Fondos Propios, Fideicomiso).</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="p-6 overflow-y-auto">
        <form id="fundingForm" @submit.prevent="handleSubmit" novalidate class="space-y-6">

          <div>
            <label :class="labelClasses">Nombre de la Fuente</label>
            <input v-model="form.name" type="text" placeholder="Ej: BCIE - Línea Pyme" required :class="inputClasses" />
          </div>

          <div>
            <label :class="labelClasses">Descripción / Notas Adicionales</label>
            <textarea v-model="form.description" rows="3" placeholder="Información sobre las condiciones del fondo..." :class="inputClasses"></textarea>
          </div>

          <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl mt-4">
            <div>
              <h4 class="text-sm font-bold text-slate-900 dark:text-slate-100">Estado de la Fuente</h4>
              <p class="text-xs text-slate-500">Permitir que sea asignada a productos de crédito.</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="form.isActive" class="sr-only peer">
              <div :class="switchClasses"></div>
            </label>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
        <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
        <button type="submit" form="fundingForm" :disabled="isSaving" :class="btnPrimary">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          {{ form.id ? 'Guardar Cambios' : 'Crear Fuente' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { X, Save, Loader2, Landmark } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({ show: Boolean, sourceToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);

// ESTILOS EN CONSTANTES
const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const switchClasses = "w-11 h-6 bg-slate-300 rounded-full peer dark:bg-slate-700 peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const form = reactive({
  id: null,
  name: '',
  description: '',
  isActive: true // Mapeado internamente a 'ACTIVE' / 'INACTIVE'
});

watch(() => props.show, (val) => {
  if (val) {
    if (props.sourceToEdit && Object.keys(props.sourceToEdit).length > 0) {
      const data = props.sourceToEdit;
      form.id = data.id;
      form.name = data.name || '';
      form.description = data.description || '';
      form.isActive = data.status !== 'INACTIVE';
    } else {
      Object.assign(form, {
        id: null, name: '', description: '', isActive: true
      });
    }
  }
});

const handleSubmit = async () => {
  if (!form.name) {
    return showError('Debe proporcionar un nombre válido para la fuente.');
  }

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');

    // Preparar el payload
    const payload = {
      company_id: user.company,
      name: form.name,
      description: form.description,
      status: form.isActive ? 'ACTIVE' : 'INACTIVE'
    };

    // RUTA CON GUION BAJO SEGÚN INSTRUCCIÓN
    if (form.id) {
      await apiClient.put(`/funding_sources/${form.id}`, payload);
      showSuccess('Fuente actualizada correctamente.');
    } else {
      await apiClient.post('/funding_sources', payload);
      showSuccess('Fuente creada correctamente.');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError(error.response?.data?.message || 'Error al guardar la fuente.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
</style>